import { apiFetch } from '@/platform/lib/api';
import { pickUpcomingEvents, upcomingEventsRange } from './eventUtils';
import type {
  CalendarAdminConfig,
  CalendarEvent,
  CalendarEventInput,
  CalendarEventRequest,
  CalendarMeta,
} from './types';

export function fetchCalendarMeta() {
  return apiFetch<CalendarMeta>('/calendar');
}

export function fetchCalendarEvents(from: string, to: string) {
  const q = new URLSearchParams({ from, to });
  return apiFetch<{ events: CalendarEvent[] }>(`/calendar/events?${q.toString()}`);
}

/** Next N upcoming site calendar events (wide date window, no month cap). */
export function fetchUpcomingCalendarEvents(limit = 10) {
  const { from, to } = upcomingEventsRange();
  return fetchCalendarEvents(from, to).then((res) => ({
    events: pickUpcomingEvents(res.events ?? [], limit),
  }));
}

export function createCalendarEvent(body: CalendarEventInput) {
  return apiFetch<CalendarEvent>('/calendar/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function updateCalendarEvent(id: string, body: Partial<CalendarEventInput>) {
  return apiFetch<CalendarEvent>(`/calendar/events/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function deleteCalendarEvent(id: string) {
  return apiFetch<{ status: string }>(`/calendar/events/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function fetchAdminCalendar() {
  return apiFetch<CalendarAdminConfig>('/admin/calendar');
}

export function patchAdminCalendar(body: Partial<CalendarAdminConfig>) {
  return apiFetch<CalendarAdminConfig>('/admin/calendar', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function submitCalendarEventRequest(body: CalendarEventInput) {
  return apiFetch<CalendarEventRequest>('/calendar/event-requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function fetchMyCalendarEventRequests() {
  return apiFetch<{ requests: CalendarEventRequest[] }>('/calendar/event-requests/mine');
}

export function fetchPendingCalendarEventRequests() {
  return apiFetch<{ requests: CalendarEventRequest[] }>('/calendar/event-requests/pending');
}

export function approveCalendarEventRequest(id: string) {
  return apiFetch<CalendarEvent>(`/calendar/event-requests/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
  });
}

export function rejectCalendarEventRequest(id: string, reason?: string) {
  return apiFetch<void>(`/calendar/event-requests/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason ?? '' }),
  });
}
